import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { withContextProvider } from '../../../hoc';
import { DialogsProvider, ToastProvider } from '../../../providers';
import { useToasts } from '../../../hooks';
import {
  createUser, updateUser, useFetchUsers, deactivateUser, resendConfirmation,
} from '../../../utils/api/users';
import { handleErrorMessages, handleToastMessages } from '../../../utils/users';
import Button from '../../../componentsV2/Button/Button';
import Pagination from '../../../componentsV2/pagination/Pagination';
import { Plus } from '../../../componentsV2/Icons/Icons';
import UsersPageWrapper from '../../../componentsV2/Users/UsersPageWrapper';
import Breadcrumbs from '../../../componentsV2/Breadcrumbs/Breadcrumbs';
import PagesHeader from '../../../componentsV2/Headers/PagesHeader';
import { logout } from '../../../utils/api/logout';
import { redirectToUrl } from '../../../utils/navigation';
import { getCurrentUserInfo } from '../../../utils/api/registrations';
import { useUrlModals } from '../../../utils/useUrlModals';
import NotFound from '../../NotFound/NotFound';

const UsersPage = ({ organization, currentUserRole }) => {
  const [users, setUsers] = useState([]); // all users data
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [userData, setUserData] = useState({}); // single user data
  const [errorMessage, setErrorMessage] = useState({
    name_first: '',
    name_last: '',
    email: '',
    role: '',
  });
  const [isDisabled, setIsDisabled] = useState(false);
  const limit = 20;
  const { showToast } = useToasts();
  const fetchUsers = useFetchUsers(organization.id, limit, currentPage, showToast, setUsers, setTotalItems);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [currentUserAuthEmail, setCurrentUserAuthEmail] = useState('');

  // Centralized modal state
  const { modals, openModal, closeModal } = useUrlModals({
    inviteUser: { isIdParam: false },
    editUser: { isIdParam: true },
    accountDetails: { isIdParam: true },
  });

  useEffect(() => {
    // Redesign users page reads current user from v3 to stay in the same API version.
    getCurrentUserInfo('v3')
      .then((response) => response.json())
      .then((data) => {
        setCurrentUserId(data.user.id);
        setCurrentUserAuthEmail(data.user.email || '');
      })
      .catch(() => {
        showToast({
          message: 'Failed to fetch current user info.',
          type: 'error',
        });
      });
  }, [showToast]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const breadcrumbLinks = [
    { href: '/admin', name: 'Admin' },
    { href: '/users', name: 'Users' },
  ];

  // Handle add/edit user submission
  const onSubmitAddEditUserModal = (userId, formData) => {
    const errors = handleErrorMessages(formData);
    if (Object.keys(errors).length > 0) {
      setErrorMessage({ ...errorMessage, ...errors });
      return;
    }
    const apiRequest = userId ? updateUser : createUser;
    const pendingEmailForVerification = formData.email;
    setIsDisabled(true);
    apiRequest(organization.id, userId, formData)
      .then((response) => response.json().then((data) => ({ data, status: response.status })))
      .then((data) => {
        if (data.data.errors) throw new Error(data.data.errors);
        if ([422, 503].includes(data.status)) {
          showToast({ message: data.data.error, type: 'error' });
          fetchUsers();
        }
        if ([200, 201].includes(data.status)) {
          const newUserData = data.data.user.user;
          const verificationEmailSentMessage = data.data.message ?? null;
          const message = handleToastMessages(userData, newUserData, userId, organization.id, verificationEmailSentMessage, pendingEmailForVerification);
          showToast({ message: message || 'Changes saved.', type: 'success' });
          if (!userId) {
            setUsers([...users, newUserData]);
          } else {
            setUsers(users.map((user) => (user.id === userId ? { ...user, ...newUserData } : user)));
          }
          closeModal('inviteUser');
          closeModal('editUser');
        }
      })
      .catch((error) => {
        const msg = error.message.startsWith('<!DOCTYPE html>')
          ? 'Something went wrong. Please reach out to the Vamos team if this error persists.'
          : error.message;
        showToast({ message: msg, type: 'error' });
      })
      .finally(() => {
        setUserData({});
        setIsDisabled(false);
      });
  };

  const handleResendEmail = (userId, orgId) => {
    resendConfirmation(userId, orgId)
      .then((response) => response.json().then((data) => {
        if (response.ok) {
          showToast({
            message:
              'The user will receive an email with instructions for how to confirm their account. If they do not receive this email, please have them check their spam folder.',
            type: 'success',
          });
        } else {
          showToast({ message: data.message, type: 'error' });
        }
      }))
      .catch((error) => showToast({ message: error.message, type: 'error' }));
  };

  const onDeactivate = (user) => {
    deactivateUser(user.id, organization.id)
      .then((response) => {
        if (!response.ok) {
          return response.json().then((err) => {
            throw new Error(err.error || 'Failed to deactivate user');
          });
        }
        return response.json();
      })
      .then(() => {
        showToast({
          message: `${user.name_first} ${user.name_last} has been removed from ${organization.name} successfully.`,
          type: 'success',
        });
        closeModal('editUser'); // Close the modal after successful deactivation
        if (user.id === currentUserId) {
          logout().then((resp) => {
            if (resp.ok) redirectToUrl('/');
          });
          return;
        }
        setUsers(users.filter((u) => u.id !== user.id));
      })
      .catch((error) => showToast({ message: error.message, type: 'error' }))
      .finally(() => setIsDisabled(false));
  };

  const editUserId = modals.editUser?.value;
  const isViewingUser = modals.editUser?.isOpen && editUserId;

  const existingUser = isViewingUser
    ? users.find(u => u.id === Number(editUserId))
    : null;

  // Use authenticated user email from /api/v3/registrations for password verification.
  // Fallback to users list only if profile payload is unavailable.
  const currentUserEmail = currentUserAuthEmail || users.find(u => u.id === currentUserId)?.email || '';

  // Check if account details modal user exists in organization
  const isValidAcc = modals.accountDetails?.value
    ? users?.some(u => u.id === Number(modals.accountDetails?.value))
    : true;

  // Only show 404 if users are loaded and user is still not found
  const notFound = (isViewingUser && !existingUser && users.length > 0) || !isValidAcc;

  useEffect(() => {
    if (!users.length) return;
    if (existingUser) {
      setUserData(existingUser);
    } else if (notFound && isValidAcc) {
      showToast({
        message: 'Failed to fetch user: User not found.',
        type: 'error',
      });
    }
  }, [existingUser, notFound, users, showToast, isValidAcc]);

  if (notFound && users.length > 0) {
    return <NotFound />;
  }

  return (
    <DialogsProvider>
      <div className="mx-2.75">
        <header>
          {/* Breadcrumbs */}
          <Breadcrumbs links={breadcrumbLinks} />
        </header>
        <div className="flex w-full justify-between items-center pb-4">
          <PagesHeader name="users" label="Users" size="h-9" />
          <Button
            label="Invite user"
            onClick={() => openModal('inviteUser')}
            Icon={Plus}
            type="primary"
            gap="gap-2.5"
          />
        </div>
        <UsersPageWrapper
          users={users}
          modals={modals}
          closeModal={closeModal}
          onOpenEditModal={(user) => openModal('editUser', user.id)}
          onOpenAccountDetailsModal={(user) => openModal('accountDetails', user.id)}
          onSubmitAddEditUserModal={onSubmitAddEditUserModal}
          onResendEmail={handleResendEmail}
          isDisabled={isDisabled}
          setIsDisabled={setIsDisabled}
          setErrorMessage={setErrorMessage}
          errorMessage={errorMessage}
          organization={organization}
          onDeactivate={onDeactivate}
          currentUserId={currentUserId}
          currentUserEmail={currentUserEmail}
          isCurrentUserManager={currentUserRole?.toLowerCase() === 'manager'}
        />

        {totalItems > limit && (
          <Pagination
            itemsPerPage={limit}
            totalItems={totalItems}
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            marginTop="mt-29.5"
          />
        )}
      </div>
    </DialogsProvider>
  );
};

UsersPage.propTypes = {
  organization: PropTypes.shape({
    id: PropTypes.number.isRequired,
    code: PropTypes.string.isRequired,
    contact: PropTypes.string.isRequired,
    contact_email: PropTypes.string.isRequired,
    description: PropTypes.string,
    is_superadmin: PropTypes.bool.isRequired,
    name: PropTypes.string.isRequired,
    status: PropTypes.number.isRequired,
  }).isRequired,
  currentUserRole: PropTypes.string.isRequired,
};

export default withContextProvider(ToastProvider)(UsersPage);
